import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";

import { Message_data } from "../context/context.js";
import { useTracks } from "../context/TracksContext.js";
import { db, auth } from "../firebase-config.js";
import {
  doc,
  collection,
  getDoc,
  addDoc,
  orderBy,
  serverTimestamp,
  onSnapshot,
  query,
  deleteDoc,
  where,
  writeBatch,
  getDocs,
  setDoc,
  updateDoc,
  getCountFromServer,
} from "firebase/firestore";
import MoodSelector from "./MoodSelector";
import AIRecommendedPanel from "./AIRecommendedPanel";
import MoodRing from "./MoodRing";
import MarqueeText from "./MarqueeText";
import { FALLBACK_COVERART } from "../lib/fallbackCoverart";

import "../styles/Chat.scss";

// Track names/titles sometimes contain underscores (e.g. carried over from
// filenames). Show them as spaces so long titles wrap naturally instead of
// forming one unbreakable string that pushes the container out of shape.
const cleanText = (s) => String(s || "").replace(/_/g, " ");

const FETCH_LYRICS_URL =
  process.env.REACT_APP_FETCH_LYRICS_URL ||
  "https://us-central1-chatbout001.cloudfunctions.net/fetchLyrics";

// Parse LRC ("[mm:ss.xx]line text") into an array of timed lines.
// Multi-stamp lines (same lyric repeated for different verses) are exploded.
function parseLrc(lrc) {
  if (!lrc || typeof lrc !== "string") return [];
  const out = [];
  const stampRe = /\[(\d{1,2}):(\d{2})(?:[.:](\d{1,3}))?\]/g;
  for (const rawLine of lrc.split(/\r?\n/)) {
    const stamps = [];
    let m;
    stampRe.lastIndex = 0;
    while ((m = stampRe.exec(rawLine)) !== null) {
      const mm = parseInt(m[1], 10);
      const ss = parseInt(m[2], 10);
      const frac = m[3] ? parseInt(m[3].padEnd(3, "0").slice(0, 3), 10) : 0;
      stamps.push(mm * 60_000 + ss * 1000 + frac);
    }
    if (!stamps.length) continue;
    const text = rawLine.replace(stampRe, "").trim();
    for (const t of stamps) out.push({ timeMs: t, text });
  }
  out.sort((a, b) => a.timeMs - b.timeMs);
  return out;
}

function trackKeyFor(currentSongObj) {
  if (!currentSongObj) return null;
  const a = (currentSongObj.artist || "").trim().toLowerCase();
  const t = (currentSongObj.track || "").trim().toLowerCase();
  if (!a || !t) return null;
  return `${a}|${t}`;
}

// Normalised "title|artist" key for matching a just-requested track to its
// queued pendingList entry — the queue doc id differs from the request's id,
// so title+artist is the reliable link.
const normTrackKey = (t, a) =>
  `${String(t || "").toLowerCase().replace(/[^a-z0-9]/g, "")}|${String(a || "").toLowerCase().replace(/[^a-z0-9]/g, "")}`;
function matchesHighlight(item, hl) {
  if (!item || !hl) return false;
  if (hl.trackId && item.id && String(item.id) === String(hl.trackId)) return true;
  if (hl.trackId && item.youTubeId && String(item.youTubeId) === String(hl.trackId)) return true;
  const ik = normTrackKey(item.track || item.title, item.artist);
  const hk = normTrackKey(hl.title, hl.artist);
  return ik !== "|" && ik === hk;
}

// Nearest scrollable ancestor of `el` (vertical). Returns null if none — so we
// can scroll ONLY that container and never let the scroll bubble up to the
// window/iframe (which would push the whole app up with no way back).
function scrollParentOf(el) {
  let node = el && el.parentElement;
  while (node) {
    try {
      const oy = window.getComputedStyle(node).overflowY;
      if ((oy === "auto" || oy === "scroll") && node.scrollHeight > node.clientHeight) return node;
    } catch (_) { /* ignore */ }
    node = node.parentElement;
  }
  return null;
}

export const NowPlaying = ({
  clientObj,
  trackObj,
  setShowNextTrackModal,
  initialTime = 10,
  currentSongObj,
  setTab,
  showDjModeModal,
  setShowDjModeModal,
  setRequestedTrack,
  pickTrack,
  highlightRequest,
  onHighlightConsumed,
}) => {
  // console.log("currentSongObj", currentSongObj);

  const [downvotes, setDownvotes] = useState(0);
  const [totalvotes, setTotalvotes] = useState(0);
  const [voteRefresh, nextVoteRefresh] = useState(false);
  const [downvotedColour, setDownvotedColour] = useState("none");
  const [upvotedColour, setUpvotedColour] = useState("none");
  const { message, setMessage } = useContext(Message_data);
  const { tracks } = useTracks();
  const [nextTrackObj, setNextTrackObj] = useState(trackObj);
  const nextTrackObjRef = useRef(trackObj);
  const [totalVotes, setTotalVotes] = useState(0);
  const [upObj, setUpObj] = useState({});
  const [downObj, setDownObj] = useState({});
  const [messages, setMessages] = useState([]);
  const [updatePendingList, setUpdatePendingList] = useState([]);
  const [selectedMoods, setSelectedMoods] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [userAvatar, setUserAvatar] = useState(null);
  const [friends, setFriends] = useState([]);
  const [showVibePanels, setShowVibePanels] = useState(() => {
    // Check if AppleWatchModal was opened (indicates app reload)
    const modalOpened = sessionStorage.getItem('appleWatchModalOpened');
    // Check if panels were previously dismissed in this session
    const dismissed = sessionStorage.getItem('vibePanelsDismissed');

    // Show panels if modal was just opened and not yet dismissed
    return modalOpened === 'true' && dismissed !== 'true';
  });
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  // New-request highlight + "you can vote" float reminder (fires when the guest
  // lands on Now Playing after making a request in the modal).
  const [highlightId, setHighlightId] = useState(null);
  const [showVoteReminder, setShowVoteReminder] = useState(false);
  const itemRefs = useRef({});
  const floatedNonceRef = useRef(null);
  const consumedNonceRef = useRef(null);

  // Current-song voting state (one vote per voter per session; the DJ's Electron app finalizes
  // the tally to playLog when the song ends, so these counts are live for the in-flight play only).
  const [currentSongUpvotes, setCurrentSongUpvotes] = useState(0);
  const [currentSongDownvotes, setCurrentSongDownvotes] = useState(0);
  const [currentSongMyVote, setCurrentSongMyVote] = useState(null); // null | "up" | "down"

  // Lyrics drawer state — fetched from LRCLIB via the fetchLyrics Cloud
  // Function, keyed by the currently-playing track. Auto-synced to the
  // approximate playhead computed from currentSongObj.startedAt when the
  // result is in LRC format.
  const [showLyrics, setShowLyrics] = useState(false);
  const [lyricsLoading, setLyricsLoading] = useState(false);
  const [lyricsError, setLyricsError] = useState(null);
  const [lyricsData, setLyricsData] = useState(null); // { found, plainLyrics, syncedLyrics }
  const [lyricsForKey, setLyricsForKey] = useState(null);
  const [lrcLines, setLrcLines] = useState([]);
  const [lrcIndex, setLrcIndex] = useState(-1);
  const lrcLineRefs = useRef([]);

  const trackKey = trackKeyFor(currentSongObj);

  const fetchLyricsNow = useCallback(async () => {
    if (!trackKey) return;
    if (lyricsForKey === trackKey && lyricsData) return;
    if (!auth?.currentUser) {
      setLyricsError("Please sign in to fetch lyrics");
      return;
    }
    setLyricsLoading(true);
    setLyricsError(null);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const resp = await fetch(FETCH_LYRICS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          title: currentSongObj?.track || "",
          artist: currentSongObj?.artist || "",
          durationSec: currentSongObj?.duration || 0,
        }),
      });
      const data = await resp.json();
      if (!resp.ok || !data?.ok) {
        throw new Error(data?.error || `HTTP ${resp.status}`);
      }
      setLyricsData(data);
      setLyricsForKey(trackKey);
      setLrcLines(parseLrc(data.syncedLyrics));
      setLrcIndex(-1);
    } catch (e) {
      console.warn("[NowPlaying] fetchLyrics failed:", e?.message);
      setLyricsError(e?.message || "Could not load lyrics");
    } finally {
      setLyricsLoading(false);
    }
  }, [trackKey, lyricsForKey, lyricsData, currentSongObj]);

  // Auto-fetch when the drawer opens, refetch when the playing track changes
  // while the drawer is open.
  useEffect(() => {
    if (!showLyrics) return;
    if (trackKey && lyricsForKey !== trackKey) {
      setLyricsData(null);
      setLrcLines([]);
      setLrcIndex(-1);
      fetchLyricsNow();
    } else if (!lyricsData && trackKey) {
      fetchLyricsNow();
    }
  }, [showLyrics, trackKey, lyricsForKey, lyricsData, fetchLyricsNow]);

  // LRC line-sync. Drive lrcIndex off the approximate playhead computed
  // from currentSongObj.startedAt. Drifts on pause/seek but acceptable for
  // a passive viewer surface; the desktop karaoke modal does true sync.
  useEffect(() => {
    if (!showLyrics || lrcLines.length === 0 || !currentSongObj?.startedAt) return;
    const startedAtMs = (() => {
      const sa = currentSongObj.startedAt;
      if (!sa) return 0;
      if (typeof sa.toMillis === "function") return sa.toMillis();
      if (typeof sa.seconds === "number") return sa.seconds * 1000;
      return 0;
    })();
    if (!startedAtMs) return;
    const tick = () => {
      const playheadMs = Date.now() - startedAtMs;
      let idx = -1;
      for (let i = 0; i < lrcLines.length; i++) {
        if (lrcLines[i].timeMs <= playheadMs) idx = i;
        else break;
      }
      setLrcIndex((prev) => (prev !== idx ? idx : prev));
    };
    tick();
    const id = setInterval(tick, 400);
    return () => clearInterval(id);
  }, [showLyrics, lrcLines, currentSongObj]);

  // Auto-scroll the active LRC line into view.
  useEffect(() => {
    if (lrcIndex < 0) return;
    const el = lrcLineRefs.current[lrcIndex];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [lrcIndex]);

  // Keep ref in sync with state
  useEffect(() => {
    nextTrackObjRef.current = nextTrackObj;
  }, [nextTrackObj]);

  // Live source of the current song, kept in a ref so the pendingList listener
  // (which subscribes once, on clientId) can read the CURRENT value. When an
  // .mbx mixtape is playing, the full tracklist is already in the queue, so the
  // single appended "next song" row is redundant and we skip it.
  const isMbxSourceRef = useRef(false);
  useEffect(() => {
    isMbxSourceRef.current = currentSongObj?.source === "mbx";
  }, [currentSongObj]);

  // Update nextTrackObj when trackObj prop changes
  useEffect(() => {
    if (trackObj) {
      setNextTrackObj(trackObj);
    }
  }, [trackObj]);

  // Update messages list when nextTrackObj changes (without re-subscribing to pendingList)
  useEffect(() => {
    if (nextTrackObj) {
      // Find and update only the "default" next track (last item in the list)
      setMessages(prevMessages => {
        // If no messages yet, just return empty (pendingList listener will populate)
        if (prevMessages.length === 0) return prevMessages;

        // Get all pending tracks (everything except the last default track)
        const pendingTracks = prevMessages.filter(msg => !msg.default);
        // During an .mbx mixtape the queue is the full tracklist — no redundant
        // single "next song" row.
        if (isMbxSourceRef.current) return pendingTracks;
        // Add the updated next track
        const updatedNextTrack = { ...nextTrackObj, default: true };
        return [...pendingTracks, updatedNextTrack];
      });
    }
  }, [nextTrackObj]);

  // Check for AppleWatchModal state on mount only (no polling)
  useEffect(() => {
    const modalOpened = sessionStorage.getItem('appleWatchModalOpened');
    const dismissed = sessionStorage.getItem('vibePanelsDismissed');

    // If modal was opened and panels aren't dismissed, show them
    if (modalOpened === 'true' && dismissed !== 'true') {
      setShowVibePanels(true);
      setIsAnimatingOut(false);
    }
  }, []);

  useEffect(() => {
    // get the pending list tracks

    const chatsMessgesRef = collection(
      db,
      "users",
      clientObj.clientId,
      "pendingList"
    );
    const queryTracks = query(chatsMessgesRef, orderBy("ordinal"));
    const unsuscribe = onSnapshot(queryTracks, (snapshot) => {
      // Create fresh array instead of mutating
      const pendingList = [];
      snapshot.forEach((doc) => {
        pendingList.push(Object.assign({ ...doc.data(), id: doc.id }));
      });
      console.log("pendingList snapshot received:", pendingList.length, "tracks");

      // Add next track to the list using ref to avoid re-subscriptions.
      // Skip it during an .mbx mixtape — the queue already holds the full
      // tracklist, so the single "next song" row would be redundant.
      const m = [...pendingList];
      if (!isMbxSourceRef.current) {
        const nextTrack = { ...nextTrackObjRef.current, default: true };
        m.push(nextTrack);
      }
      setMessages(m);
    });

    return () => {
      unsuscribe();
    };
  }, [clientObj.clientId]); // Removed nextTrackObj dependency - using ref instead to avoid re-subscriptions

  // --- New-request highlight + vote reminder --------------------------------
  // `highlightRequest` carries the just-requested track's identity + a one-shot
  // nonce from Jukebox4. Flash that track in the queue and float a big up-vote
  // icon up the screen to remind the guest they can vote.
  useEffect(() => {
    const hl = highlightRequest;
    if (!hl || !hl.nonce || consumedNonceRef.current === hl.nonce) return;
    // Float the reminder once per request.
    if (floatedNonceRef.current !== hl.nonce) {
      floatedNonceRef.current = hl.nonce;
      setShowVoteReminder(true);
    }
    // The request may reach the pendingList a beat later — this re-runs as
    // `messages` updates until the matching queue row appears.
    const match = messages.find((m) => !m.default && matchesHighlight(m, hl));
    if (match) {
      consumedNonceRef.current = hl.nonce;
      const id = match.id;
      setHighlightId(id);
      requestAnimationFrame(() => {
        const el = itemRefs.current[id];
        if (!el) return;
        const container = scrollParentOf(el);
        if (container) {
          // Scroll just this container — bring the row toward the middle,
          // never touching the window/iframe scroll position.
          const cRect = container.getBoundingClientRect();
          const eRect = el.getBoundingClientRect();
          const delta = (eRect.top - cRect.top) - (container.clientHeight - el.clientHeight) / 2;
          container.scrollBy({ top: delta, behavior: "smooth" });
        } else if (typeof el.scrollIntoView === "function") {
          // No inner scroller found — fall back to the gentlest option that
          // won't overscroll ancestors.
          el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      });
      if (onHighlightConsumed) onHighlightConsumed();
    }
  }, [highlightRequest, messages, onHighlightConsumed]);

  // Auto-hide the float reminder after it plays.
  useEffect(() => {
    if (!showVoteReminder) return undefined;
    const t = setTimeout(() => setShowVoteReminder(false), 3200);
    return () => clearTimeout(t);
  }, [showVoteReminder]);

  // Auto-clear the row flash once it has played.
  useEffect(() => {
    if (!highlightId) return undefined;
    const t = setTimeout(() => setHighlightId(null), 2800);
    return () => clearTimeout(t);
  }, [highlightId]);

  // Safety valve: if the request never lands in the queue (DJ hasn't accepted
  // it yet), still consume the nonce so it can't re-fire on a later visit.
  useEffect(() => {
    const hl = highlightRequest;
    if (!hl || !hl.nonce || consumedNonceRef.current === hl.nonce) return undefined;
    const t = setTimeout(() => {
      consumedNonceRef.current = hl.nonce;
      if (onHighlightConsumed) onHighlightConsumed();
    }, 12000);
    return () => clearTimeout(t);
  }, [highlightRequest, onHighlightConsumed]);

  // Live tally of votes for the currently playing song (scoped to the active sessionId).
  // Stale votes from previous plays are ignored — the DJ's Electron app sweeps them when the
  // song transitions, but we filter defensively here too.
  useEffect(() => {
    const sessionId = currentSongObj?.sessionId;
    if (!clientObj?.clientId || !sessionId) {
      setCurrentSongUpvotes(0);
      setCurrentSongDownvotes(0);
      setCurrentSongMyVote(null);
      return;
    }

    const votesRef = collection(db, "users", clientObj.clientId, "currentSongVotes");
    const unsubscribe = onSnapshot(votesRef, (snapshot) => {
      let up = 0;
      let down = 0;
      let mine = null;
      const myUid = auth.currentUser?.uid;
      snapshot.forEach((d) => {
        const v = d.data();
        if (v.sessionId !== sessionId) return;
        if (v.voted === "up") up++;
        else if (v.voted === "down") down++;
        if (myUid && d.id === myUid) mine = v.voted || null;
      });
      setCurrentSongUpvotes(up);
      setCurrentSongDownvotes(down);
      setCurrentSongMyVote(mine);
    });

    return () => unsubscribe();
  }, [clientObj?.clientId, currentSongObj?.sessionId]);

  // Cast / change the current user's vote on the currently playing song.
  // Doc id = voter UID, so re-voting just overwrites — enforcing one vote per voter per session.
  const voteCurrentSong = async (direction) => {
    if (!auth.currentUser) {
      alert("Sign in to vote on the current song");
      return;
    }
    const sessionId = currentSongObj?.sessionId;
    if (!sessionId) {
      console.warn("voteCurrentSong: no sessionId on currentSongObj");
      return;
    }
    try {
      const voteRef = doc(
        db,
        "users",
        clientObj.clientId,
        "currentSongVotes",
        auth.currentUser.uid
      );
      await setDoc(voteRef, {
        sessionId,
        voted: direction, // "up" | "down"
        votedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error voting on current song:", error);
    }
  };

  // Load user's mood and genre selections
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (!auth.currentUser) return;

      try {
        const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();

          // Load moods and genres
          if (userData.selectedMoods) {
            setSelectedMoods(userData.selectedMoods);
          }
          if (userData.selectedGenres) {
            setSelectedGenres(userData.selectedGenres);
          }

          // Load user avatar
          if (userData.photoURL) {
            setUserAvatar(userData.photoURL);
          }
        }
      } catch (error) {
        console.error("Error loading user preferences:", error);
      }
    };

    loadUserPreferences();
  }, []);

  // Helper function to check if a Firestore timestamp is from today
  const isToday = (timestamp) => {
    if (!timestamp || !timestamp.toDate) return false;

    const date = timestamp.toDate();
    const today = new Date();

    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Load user's friends from chats with caching
  useEffect(() => {
    if (!auth.currentUser) return;

    const chatsRef = collection(db, "users", auth.currentUser.uid, "chats");
    const friendCache = new Map(); // Cache with 5-minute expiry
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

    const unsubscribe = onSnapshot(chatsRef, async (snapshot) => {
      const friendsMap = new Map();
      const now = Date.now();

      // Extract unique friend IDs from all chats
      snapshot.forEach((chatDoc) => {
        const chatData = chatDoc.data();
        if (chatData.users && Array.isArray(chatData.users)) {
          chatData.users.forEach((user) => {
            // Add all users except current user
            if (user.id && user.id !== auth.currentUser.uid) {
              friendsMap.set(user.id, {
                id: user.id,
                displayName: user.displayName || "Unknown",
                photoURL: user.photoURL || null,
                email: user.email || null
              });
            }
          });
        }
      });

      // Convert Map to array
      const friendsArray = Array.from(friendsMap.values());

      // Fetch additional profile data only for uncached or stale friends
      const friendsWithMoods = await Promise.all(
        friendsArray.map(async (friend) => {
          try {
            // Check cache first
            const cached = friendCache.get(friend.id);
            if (cached && (now - cached.timestamp) < CACHE_DURATION) {
              return cached.data;
            }

            // Fetch from Firebase if not cached or stale
            const userDoc = await getDoc(doc(db, "users", friend.id));
            if (userDoc.exists()) {
              const userData = userDoc.data();

              // Only include friends who were active today (based on updatedAt timestamp)
              if (!isToday(userData.updatedAt)) {
                return null; // Filter out friends not active today
              }

              const friendData = {
                ...friend,
                selectedMoods: userData.selectedMoods || [],
                photoURL: userData.photoURL || friend.photoURL,
                updatedAt: userData.updatedAt
              };

              // Cache the result
              friendCache.set(friend.id, {
                data: friendData,
                timestamp: now
              });

              return friendData;
            }
            return null;
          } catch (error) {
            console.error(`Error fetching data for friend ${friend.id}:`, error);
            return null;
          }
        })
      );

      // Filter out null values (friends not active today)
      const activeFriendsToday = friendsWithMoods.filter(friend => friend !== null);

      setFriends(activeFriendsToday);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Ensure clientObj.clientId exists before setting up listeners
    if (!clientObj?.clientId) {
      console.warn("NowPlaying: clientObj.clientId not available yet");
      return;
    }

    const init = async () => {
      // collate votes for all pending tracks

      const votesRef = collection(
        db,
        "users",
        clientObj.clientId,
        "pendingListVotes"
      );

      // Helper function to process vote data
      const processVotes = (querySnapshot) => {
        const voteUpObj = {};
        const voteDownObj = {};
        const votes = [];

        querySnapshot.forEach((doc) => {
          const voteData = doc.data();
          if (voteData.votedAt !== null) {
            let trackId = voteData.trackId;
            // Create fresh vote object instead of pushing reference
            votes.push({ ...voteData });

            if (voteData.voted === "up") {
              voteUpObj[trackId] = (voteUpObj[trackId] || 0) + 1;
            } else {
              voteDownObj[trackId] = (voteDownObj[trackId] || 0) + 1;
            }
          }
        });

        console.log("Processed votes - Up:", Object.keys(voteUpObj).length, "Down:", Object.keys(voteDownObj).length, "Total:", votes.length);

        // Update state immediately without setTimeout
        setTotalVotes(votes);
        setDownObj(voteDownObj);
        setUpObj(voteUpObj);
      };

      // Set up real-time listener for vote changes
      // Note: onSnapshot fires immediately with current data, then on each change
      const unsubscribe = onSnapshot(
        votesRef,
        (snapshot) => {
          console.log("Votes snapshot received:", snapshot.size, "votes");
          processVotes(snapshot);
        },
        (error) => {
          console.error("Error in votes listener:", error);
        }
      );

      // Add visibility change listener for mobile recovery
      const handleVisibilityChange = async () => {
        if (!document.hidden) {
          // App regained focus - manually refetch votes
          console.log("App resumed, refetching votes...");
          try {
            const snapshot = await getDocs(votesRef);
            processVotes(snapshot);
          } catch (error) {
            console.error("Error refetching votes on resume:", error);
          }
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        unsubscribe();
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      };
    };
    init();
  }, [clientObj?.clientId]);

  useEffect(() => {
    const init = async () => {
      const nextDocRef = doc(
        db,
        "users",
        clientObj.clientId,
        "nextSong",
        "NEXTSONG"
      );
      const docNextSongSnap = await getDoc(nextDocRef);
      if (docNextSongSnap.exists()) {
        const voted = docNextSongSnap.data().voted || [];
        // console.log("voted", voted.length);
        setTotalvotes(voted.length);
        const uv = docNextSongSnap.data().upvotes || 0;
        // setUpvotes(uv);
        const dv = docNextSongSnap.data().downvotes || 0;
        setDownvotes(dv);
      }
    };
    init();
  }, [clientObj.clientId, upvotedColour, downvotedColour, voteRefresh]);

  useEffect(() => {
    const playerControlData = async () => {
      if (message) {
        const data = JSON.parse(message);
        if (data.newtrackupdate) {
          const newtrackupdate = JSON.parse(data.newtrackupdate);
          console.log("newtrackupdate", newtrackupdate);
          setNextTrackObj(newtrackupdate);
          nextVoteRefresh(!voteRefresh);
        }
        //
        if (data.pendingListUpdate) {
          const pendingListUpdate = JSON.parse(data.pendingListUpdate);
          console.log("pendingListUpdate", pendingListUpdate);
          setUpdatePendingList(pendingListUpdate);
          nextVoteRefresh(!voteRefresh);
        }
      }
    };
    playerControlData();
  }, [message]);

  // Animation handler for panel dismissal
  const handlePanelDismiss = useCallback((onComplete) => {
    setIsAnimatingOut(true);

    // After animation completes, hide panels and run callback
    setTimeout(() => {
      setShowVibePanels(false);
      sessionStorage.setItem('vibePanelsDismissed', 'true'); // Persist dismissal until app reload
      setIsAnimatingOut(false);
      if (onComplete) onComplete();
    }, 400); // Match animation duration
  }, []);

  // Handler for AI panel track clicks
  const handleAITrackClick = useCallback((track, index) => {
    handlePanelDismiss(() => {
      // Process request after animation
      if (setRequestedTrack) setRequestedTrack(track);
      if (pickTrack) {
        const syntheticEvent = { preventDefault: () => {} };
        pickTrack(syntheticEvent, track, index);
      }
    });
  }, [handlePanelDismiss, setRequestedTrack, pickTrack]);

  // Handler for Request button
  const handleRequestClick = useCallback(() => {
    handlePanelDismiss(() => {
      // Navigate to requests tab after animation
      setTab("requests");
    });
  }, [handlePanelDismiss, setTab]);

  const upvote = async (track) => {
    // to determine if this user has already voted for this track lets look it up on the pendingListVotes collection
    // if the trackId and the voter/auth.currentUser.uid match then this voter cannot vote on this track again
    const notVoted = await checkVotingMatches(track.id, auth.currentUser.uid);
    if (notVoted) {
      const refDoc = collection(
        db,
        "users",
        clientObj.clientId,
        "pendingListVotes"
      );
      try {
        await addDoc(refDoc, {
          trackId: track.id,
          voter: auth.currentUser.uid,
          voted: "up",
          votedAt: serverTimestamp(),
        });

        // return newprod;
        // lets inform all the other people who voted for this track in a chat
        // send an individual chat to each person found and then highlight the
        // chat button in 2nd layout so people know theres a message waiting

        // console.log("**==** track.id", track.id);
        // console.log("**==** auth.currentUser.uid", auth.currentUser.uid);
        // console.log("**==** totalVotes", totalVotes);

        // const alsoVotedList = totalVotes.map((item) => {
        //   if (item.trackId === track.id && item.voted === "up") {
        //     return item;
        //   }
        // });

        // eslint-disable-next-line array-callback-return
        const alsoVotedList = totalVotes.filter((item) => {
          // console.log("item", item);
          if (item.trackId === track.id && item.voted === "up") {
            // can just shoot off a context message right here to message  the one found
            // get user object from item.voter
            addToFriendsList(item.voter, track);
            console.log("**==** other voter", item.voter);
            return item;
          }
        });

        console.log("**==** alsoVotedList", alsoVotedList);

        ////////////////////////////////////////////////////
      } catch (error) {
        console.error("Error adding document: ", error);
      }
    } else {
      alert("you have already voted on this track");
    }
  };

  // people who voted in common
  const addToFriendsList = async (uid, track) => {
    // console.log("**==** voter uid", uid);
    // e.preventDefault();

    let user = {};
    const docRef = doc(db, "users", uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      user = {
        id: uid,
        displayName: docSnap.data().displayName,
        photoURL: docSnap.data().photoURL,
        email: docSnap.data().email,
      };
      // console.log("**==** got voter user details", user);
      // const isthisemail = docSnap.data().email;
    }

    const chatRef = collection(db, "users", auth.currentUser.uid, "chats");
    try {
      // console.log(
      //   "**==**  find out if this user is already in current user chats",
      //   user
      // );
      const { docs } = await getDocs(chatRef);
      let chatFound = null;
      // console.log("**==** docs", docs);
      docs.forEach((doc) => {
        const userList = doc.data().users;
        const i = userList.findIndex(
          (chatuser) =>
            chatuser.id === user.id && chatuser.id !== auth.currentUser.uid
        );
        if (i !== -1) {
          chatFound = doc.id;
        }
      });
      if (chatFound !== null) {
        // console.log("**==** chat to be opened", chatFound);
        // just send the user a message since the chat already exists
        const messagesRef4 = collection(db, "chats", chatFound, "messages");
        console.log(
          "**==** send them the first chats messages using chatFound",
          chatFound
        );
        await addDoc(messagesRef4, {
          text: "We voted for the same song.",
          votedFor: track,
          createdAt: serverTimestamp(),
          user: auth.currentUser.displayName,
          uid: auth.currentUser.uid,
        });
        // get the total number of messages here
        const snapshot = await getCountFromServer(messagesRef4);
        // console.log("count: ", snapshot.data().count);
        const messageLength = snapshot.data().count;

        // make sure the unread storage is updated
        setTimeout(async () => {
          // updateWatchList(chatFound, user.id);

          //now go look for all the other chat users personal chat listand update the updatedAt field
          // so it can be snapped on in ChatList

          const otherUsersRef = doc(
            db,
            "users",
            auth.currentUser.uid,
            "chats",
            chatFound
          );
          const otherUsersDoc = await getDoc(otherUsersRef);
          if (otherUsersDoc.exists) {
            const otherUserList = otherUsersDoc.data().users;
            otherUserList.forEach(async (item, i) => {
              try {
                const updatedAt = {
                  chatLength: messageLength,
                };
                const otherItemRef = doc(
                  db,
                  "users",
                  item.id,
                  "chats",
                  chatFound
                );
                const otherItem = await getDoc(otherItemRef);
                if (otherItem.exists()) {
                  await updateDoc(otherItemRef, { ...updatedAt });
                }
              } catch (err) {
                console.log(err);
              }
            });
          }
        }, 500);
      } else {
        // make a new chat featuring this picked user and the current logged in user
        const chatStoreRef = collection(db, "chats");
        const newChatStoreId = await addDoc(chatStoreRef, {
          createdAt: serverTimestamp(),
          type: "private", // or group
        });
        const chatRefRef = doc(
          db,
          "users",
          auth.currentUser.uid,
          "chats",
          newChatStoreId.id
        );
        // console.log("**==** new chat newChatStoreId.id", newChatStoreId.id);
        // console.log("**==** put this user and current user into it", user);
        await setDoc(chatRefRef, {
          createdAt: serverTimestamp(),
          type: "private", // or group
          newchat: 1,
          users: [
            {
              id: auth.currentUser.uid || "",
              email: auth.currentUser.email || "",
              displayName: auth.currentUser.displayName || "",
            },
            {
              id: user.id || "",
              email: user.email || "",
              displayName: user.displayName || "",
            },
          ],
          chatId: newChatStoreId.id,
        });

        const chatRefRef2 = doc(
          db,
          "users",
          user.id,
          "chats",
          newChatStoreId.id
        );
        // console.log("**==** do the same in the other users chats table", user);
        await setDoc(chatRefRef2, {
          createdAt: serverTimestamp(),
          type: "private", // or group
          users: [
            {
              id: user.id || "",
              email: user.email || "",
              displayName: user.displayName || "",
            },
            {
              id: auth.currentUser.uid || "",
              email: auth.currentUser.email || "",
              displayName: auth.currentUser.displayName || "",
            },
          ],
          chatId: newChatStoreId.id,
        });

        const messagesRef4 = collection(
          db,
          "chats",
          newChatStoreId.id,
          "messages"
        );
        console.log(
          "**==** send them the new chats messages using newChatStoreId.id",
          newChatStoreId.id
        );
        await addDoc(messagesRef4, {
          text: "We voted for the same song.",
          votedFor: track,
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          user: user.displayName, //auth.currentUser.displayName || auth.currentUser.email,
          uid: user.id, //auth.currentUser.uid,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  // const updateWatchList = async (chatId, userId) => {
  //   const updatewatchListRef2 = doc(
  //     db,
  //     "users",
  //     userId,
  //     "updatewatchList",
  //     chatId
  //   );

  //   try {
  //     const thisChat2 = {
  //       chatId: chatId,
  //       updatedAt: serverTimestamp(),
  //       //chatLength: msgsLength,
  //     };
  //     const updatewatchListDocSnap = await getDoc(updatewatchListRef2);
  //     // get the message length

  //     if (updatewatchListDocSnap.exists()) {
  //       console.log(
  //         "updatewatchListDocSnap.data()",
  //         updatewatchListDocSnap.data()
  //       );
  //       thisChat2.chatLength =
  //         parseInt(updatewatchListDocSnap.data().chatLength) + 1;
  //       await setDoc(
  //         doc(db, "users", userId, "updatewatchList", chatId),
  //         thisChat2
  //       );
  //     } else {
  //       console.log(
  //         "updatewatchListDocSnap.data()",
  //         updatewatchListDocSnap.data()
  //       );
  //       thisChat2.chatLength = 1;
  //       await setDoc(
  //         doc(db, "users", userId, "updatewatchList", chatId),
  //         thisChat2
  //       );
  //     }
  //   } catch (err) {
  //     console.log(err);
  //   }
  // };

  const downvote = async (track) => {
    const notVoted = await checkVotingMatches(track.id, auth.currentUser.uid);
    // check if this vote is minus 3
    // if it is minus 3 then change

    const dv = downObj[track.id] || 0;
    const uv = upObj[track.id] || 0;

    // console.log("downObj[track.id]+1", dv);
    // console.log("upObj[track.id]", uv);

    // if (dv + 1 - uv >= 3) {
    //   // console.log("this track should be taken out of list");
    //   bumpThisTrack(track);
    // } else 
    if (notVoted) {
      const refDoc = collection(
        db,
        "users",
        clientObj.clientId,
        "pendingListVotes"
      );
      try {
        await addDoc(refDoc, {
          trackId: track.id,
          voter: auth.currentUser.uid,
          voted: "down",
          votedAt: serverTimestamp(),
        });
        // return newprod;
      } catch (error) {
        console.error("Error adding document: ", error);
      }
    } else {
      alert("you have already voted on this track");
    }
  };

  const checkVotingMatches = async (trackId, uid) => {
    // console.log("totalVotes", totalVotes);
    if (totalVotes.length > 0) {
      // console.log("uid", uid);
      const idx = totalVotes.findIndex(
        (item) => item.trackId === trackId && item.voter === uid
      );
      if (idx !== -1) {
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
  };

  const bumpThisTrack = async (track) => {
    // update a record that the autoplay can read and therefore take this track out
    // of pendinglist and/or being next to play (if its a default next track it has
    // to be passed over,  if its a request it can be taken out of jukebox list in autoplay)
    //
    if (track.default) {
      // console.log("this is a default track");

      const skippableRef = collection(
        db,
        "users",
        clientObj.clientId,
        "skippable"
      );

      const data = {
        ...track,
        createAt: serverTimestamp(),
      };

      try {
        await addDoc(skippableRef, data);
      } catch (error) {
        console.error("Error adding document: ", error);
      }
    } else {
      // console.log("this isi not a default track");
      // simply delete this track and its votes
      // from pendingList

      const init = async () => {
        // update pending collection for this user with newPending
        const plv = [];
        // console.log("this hsould be an array: oldPending", oldPending);
        if (oldPending.length > 0) {
          oldPending.forEach((item) => {
            // console.log("delete item", item);
            if (item) {
              const ref = doc(
                db,
                `users`,
                clientObj.clientId,
                "pendingList",
                item.id
              );
              deleteDoc(ref);
              plv.push(item);
            }
          });

          // now get rid of plv
          // console.log("plv", plv);
          // console.log("plv length", plv.length);
          if (plv.length > 0) {
            plv.forEach(async (plvitem) => {
              // console.log("plv item", plvitem);

              const collectionRef = collection(
                db,
                "users",
                clientObj.clientId,
                "pendingListVotes"
              );
              const q = query(
                collectionRef,
                where("trackId", "==", plvitem.id)
              );

              const querySnapshot = await getDocs(q);
              const batch = writeBatch(db);

              querySnapshot.forEach((doc) => {
                batch.delete(doc.ref);
              });

              await batch.commit();
              // console.log("Documents deleted successfully");
            });
          }
          const ref2 = doc(
            db,
            `users`,
            clientObj.clientId,
            "deleteRequest",
            "DELETEREQUEST"
          );
          setDoc(ref2, {
            muuzboxDelSong: track,
            updatedAt: serverTimestamp(),
          });
        }
      };
      const oldPending = [];
      oldPending.push(track);
      init();
    }
  };

  const [timeLeft, setTimeLeft] = useState(initialTime);
  const timerRef = useRef(null);

  // Function to reset countdown
  const resetTimer = () => {
    // Check and clear existing timer before creating new one
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setTimeLeft(initialTime); // Reset time
    startTimer(); // Restart timer
  };

  // Start countdown
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const startTimer = useCallback(() => {
    // Clear any existing timer before creating new one
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current); // Stop at 0
          timerRef.current = null; // Set to null after clearing
          setShowNextTrackModal(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  });

  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current); // Cleanup on unmount
        timerRef.current = null; // Set to null after clearing
      }
    };
  }, [startTimer]);

  return (
    <>
      <style>{`
        /* New-request row flash — a couple of quick blue flashes, then a slow fade. */
        @keyframes muuzNewReqFlash {
          0%   { background: rgba(59,130,246,0.55); box-shadow: 0 0 18px rgba(59,130,246,0.85); }
          12%  { background: rgba(59,130,246,0.05); }
          24%  { background: rgba(59,130,246,0.55); }
          36%  { background: rgba(59,130,246,0.05); }
          48%  { background: rgba(59,130,246,0.45); box-shadow: 0 0 14px rgba(59,130,246,0.6); }
          100% { background: rgba(59,130,246,0);    box-shadow: 0 0 0 rgba(59,130,246,0); }
        }
        .muuz-newreq-flash { animation: muuzNewReqFlash 2.6s ease-out forwards; border-radius: 12px; }
        /* Google-Meet-style up-vote reaction floating up the screen. */
        @keyframes muuzVoteRise {
          0%   { transform: translateY(0) scale(0.5); opacity: 0; }
          12%  { opacity: 0.5; }
          85%  { opacity: 0.45; }
          100% { transform: translateY(-72vh) scale(1.15); opacity: 0; }
        }
        @keyframes muuzVoteWiggle {
          0%,100% { transform: translateX(0); }
          25%     { transform: translateX(-12px); }
          75%     { transform: translateX(12px); }
        }
        .muuz-vote-float { position: absolute; bottom: 6%; animation: muuzVoteRise 3s ease-out forwards; will-change: transform, opacity; }
        .muuz-vote-float svg { animation: muuzVoteWiggle 1.1s ease-in-out infinite; display: block; filter: drop-shadow(0 4px 10px rgba(0,0,0,0.4)); }
        @keyframes muuzVoteHint {
          0%   { opacity: 0; transform: translate(-50%, 10px); }
          15%  { opacity: 1; transform: translate(-50%, 0); }
          80%  { opacity: 1; }
          100% { opacity: 0; }
        }
        .muuz-vote-hint {
          position: absolute; left: 50%; bottom: 14%; transform: translateX(-50%);
          background: rgba(17,24,39,0.85); color: #fff; font-weight: 700; font-size: 0.95rem;
          padding: 8px 16px; border-radius: 999px; white-space: nowrap;
          box-shadow: 0 6px 18px rgba(0,0,0,0.4); animation: muuzVoteHint 3.2s ease-out forwards;
        }
      `}</style>
      {/* DJ Mode Modal */}
      {showDjModeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#2a2a2a',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '400px',
            textAlign: 'center',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <p style={{ color: '#ffffff', fontSize: '1.1rem', marginBottom: '1.5rem' }}>
              Check your{' '}
              <span 
                onClick={() => {
                  setTab('chat');
                  setShowDjModeModal(false);
                }}
                style={{
                  color: '#007bff',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontWeight: 'bold'
                }}
              >
                chat
              </span>
              {' '}for replies to your requests
            </p>
            <button
              onClick={() => setShowDjModeModal(false)}
              style={{
                backgroundColor: '#007bff',
                color: '#ffffff',
                border: 'none',
                padding: '0.5rem 1.5rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col overflow-x-auto mb-4 manuf-scrollable-section">
        <div className="flex flex-row  h-full">
          <div className="text-white divide-y divide-gray-100 w-full  muuz-nowplaying-panel">

            <div>
              {/* <div className="flex justify-between text-white">
                <div>&npsp;</div>
                <div className="text-black font-black">
                  <h1>NEXT TRACK</h1>
                </div>
                <button
                  onClick={() => {
                    setShowNextTrackModal(false);
                  }}
                  className="block text-center flex justify-end px-4 py-2 text-black hover:bg-gray-600 dark:hover:bg-gray-600 dark:hover:text-white"
                >
                  <div>X</div>
                </button>
              </div> */}

              <div className="tab-content active song playing" id="playing">
                <div className="flex flex-row justify-between text-white">
                  <img
                    src={currentSongObj?.coverart || FALLBACK_COVERART}
                    alt="cover art"
                    className="w-full lg:w-1/2 rounded-full mr-4"
                    style={{ width: "64px", height: "64px" }}
                    onError={(e) => {
                      const src = e.target.getAttribute('data-original-src');
                      if (!src) {
                        e.target.setAttribute('data-original-src', e.target.src);
                        e.target.src = FALLBACK_COVERART;
                        setTimeout(() => {
                          e.target.src = e.target.getAttribute('data-original-src');
                        }, 3000);
                      } else {
                        e.target.onerror = null;
                        e.target.src = FALLBACK_COVERART;
                      }
                    }}
                  />
                  <div className="flex-grow" style={{ minWidth: 0, textAlign: "left" }}>
                    <p className="title text-white text-left">
                      <MarqueeText
                        text={cleanText(currentSongObj?.track) || "Unknown Track"}
                        textStyle={{ fontWeight: "bold" }}
                      />
                    </p>
                    <p className="title text-white text-left">
                      <MarqueeText
                        text={cleanText(currentSongObj?.artist) || "Unknown Artist"}
                      />
                    </p>
                    <div className="play-indicator">
                      <div className="play-icon">
                        <svg
                          style={{ color: "white" }}
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path
                            d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"
                            fill="red"
                          ></path>
                        </svg>
                      </div>
                      <span style={{ fontSize: "1.05rem", fontWeight: 700 }}>
                        Now Playing
                      </span>
                    </div>
                  </div>
                  {/* Current song vote controls + lyrics. Votes render only with a
                      sessionId; the Lyrics button always shows below them, stretched
                      to match the width of the vote buttons + counts. */}
                  <div className="flex flex-col items-center mt-0">
                    {currentSongObj?.sessionId && (
                      <div style={{ display: "grid", gridTemplateColumns: "28px 34px", columnGap: 6, rowGap: 8, alignItems: "center", marginBottom: 8 }}>
                        <button
                          onClick={() => voteCurrentSong("up")}
                          aria-label="Upvote current song"
                          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 24, padding: 0, background: "transparent", border: "none", cursor: "pointer" }}
                        >
                          <svg
                            width="20px"
                            height="20px"
                            viewBox="0 0 24 24"
                            fill={currentSongMyVote === "up" ? "#0000FF" : "#FFFFFF"}
                            fillOpacity={currentSongMyVote === "up" ? "1" : "0"}
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M7.99997 20H17.1919C17.9865 20 18.7058 19.5296 19.0243 18.8016L21.8323 12.3833C21.9429 12.1305 22 11.8576 22 11.5816V11C22 9.89543 21.1045 9 20 9H13.5L14.7066 4.5757C14.8772 3.95023 14.5826 3.2913 14.0027 3.00136V3.00136C13.4204 2.7102 12.7134 2.87256 12.3164 3.3886L8.41472 8.46082C8.14579 8.81044 7.99997 9.23915 7.99997 9.68024V20ZM7.99997 20H2V10H7.99997V20Z"
                              stroke="#FFFFFF"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <div style={{ textAlign: "left", fontVariantNumeric: "tabular-nums" }}>{currentSongUpvotes}</div>
                        <button
                          onClick={() => voteCurrentSong("down")}
                          aria-label="Downvote current song"
                          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 24, padding: 0, background: "transparent", border: "none", cursor: "pointer" }}
                        >
                          <svg
                            width="20px"
                            height="20px"
                            viewBox="0 0 24 24"
                            fill={currentSongMyVote === "down" ? "#FF0000" : ""}
                            fillOpacity={currentSongMyVote === "down" ? "1" : "0"}
                            xmlns="http://www.w3.org/2000/svg"
                            transform="scale(-1, 1)"
                          >
                            <path
                              d="M7.99997 4H17.1919C17.9865 4 18.7058 4.47039 19.0243 5.19836L21.8323 11.6167C21.9429 11.8695 22 12.1424 22 12.4184V13C22 14.1046 21.1045 15 20 15H13.5L14.7066 19.4243C14.8772 20.0498 14.5826 20.7087 14.0027 20.9986V20.9986C13.4204 21.2898 12.7134 21.1274 12.3164 20.6114L8.41472 15.5392C8.14579 15.1896 7.99997 14.7608 7.99997 14.3198V14M7.99997 4H2V14H7.99997M7.99997 4V14"
                              stroke="#FFFFFF"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <div style={{ textAlign: "left", fontVariantNumeric: "tabular-nums" }}>{currentSongDownvotes}</div>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowLyrics(true)}
                      aria-label="Show lyrics"
                      disabled={!trackKey}
                      style={{
                        width: "100%",
                        marginTop: 2,
                        padding: "6px 12px",
                        fontSize: "0.85rem",
                        fontWeight: 700,
                        color: "#1a1305",
                        background: "linear-gradient(135deg,#f59e0b,#f0a020)",
                        border: "none",
                        borderRadius: 999,
                        boxShadow: "0 3px 10px rgba(245,158,11,0.35)",
                        cursor: trackKey ? "pointer" : "not-allowed",
                        opacity: trackKey ? 1 : 0.4,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Lyrics
                    </button>
                  </div>
                </div>
              </div>

              {/* Wrap both panels for synchronized animation */}
              {showVibePanels && (
                <>
                  {/* Mood Ring - shows user's mood visualization with avatar */}
                  {selectedMoods.length > 0 ? (
                    <motion.div
                      className="px-4 pt-4"
                      initial={{ opacity: 1, y: 0 }}
                      animate={isAnimatingOut ? { opacity: 0, y: -50 } : { opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }}
                    >
                      <MoodRing
                        selectedMoods={selectedMoods}
                        userAvatar={userAvatar}
                        friends={friends}
                        onRequestClick={handleRequestClick}
                      />
                    </motion.div>
                  ) : null}

                  {/* AI Recommended Panel - shows recommendations based on user's moods/genres */}
                  {selectedMoods.length > 0 || selectedGenres.length > 0 ? (
                    <motion.div
                      className="pt-4"
                      initial={{ opacity: 1, y: 0 }}
                      animate={isAnimatingOut ? { opacity: 0, y: -50 } : { opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }}
                    >
                      <AIRecommendedPanel
                        selectedMoods={selectedMoods}
                        selectedGenres={selectedGenres}
                        tracks={tracks}
                        clientObj={clientObj}
                        onTrackClick={handleAITrackClick}
                      />
                    </motion.div>
                  ) : null}
                </>
              )}

              {/* Songs in Queue Title */}
              <h2
                className="font-semibold text-white mb-2 px-2 text-left mt-4"
                style={{ fontSize: "11px", letterSpacing: "0.06em", textTransform: "uppercase", opacity: 0.7 }}
              >
                Songs in Queue
              </h2>

              <div className="flex flex-wrap justify-left mt-2 h-20 overflow">
                <div className="w-full lg:w-1/2 pb-48">
                  {messages.map((item, i) => {
                    // Calculate if track should be greyed out (3+ downvotes)
                    const downvoteCount = downObj[item.id] || 0;
                    const upvoteCount = upObj[item.id] || 0;
                    const netDownvotes = downvoteCount - upvoteCount;
                    const isGreyedOut = netDownvotes >= 3;
                    // Desktop-controlled layout: compact divided rows vs cards.
                    const isCompactLayout = clientObj?.nowPlayingCompactLayout === true;

                    return isCompactLayout ? (
                    <div
                      key={i}
                      ref={(el) => { if (el) itemRefs.current[item.id] = el; }}
                      className={"flex items-center py-3 px-2" + (highlightId && highlightId === item.id ? " muuz-newreq-flash" : "")}
                      style={{
                        borderBottom: "1px solid rgba(255,255,255,0.08)",
                        opacity: isGreyedOut ? 0.4 : 1,
                        transition: "opacity 0.3s ease",
                      }}
                    >
                      <span
                        className="text-gray-300 text-sm font-black mr-2"
                        style={{ minWidth: 18, flexShrink: 0 }}
                      >
                        {i + 1}.
                      </span>
                      <img
                        src={item.coverart || FALLBACK_COVERART}
                        alt="cover art"
                        className="rounded-full object-cover mr-2"
                        style={{
                          width: "44px",
                          height: "44px",
                          flexShrink: 0,
                          filter: isGreyedOut ? "grayscale(100%)" : "none",
                        }}
                        onError={(e) => {
                          const src = e.target.getAttribute("data-original-src");
                          if (!src) {
                            e.target.setAttribute("data-original-src", e.target.src);
                            e.target.src = FALLBACK_COVERART;
                            setTimeout(() => {
                              e.target.src = e.target.getAttribute("data-original-src");
                            }, 3000);
                          } else {
                            e.target.onerror = null;
                            e.target.src = FALLBACK_COVERART;
                          }
                        }}
                      />
                      <div className="flex-1" style={{ minWidth: 0, textAlign: "left" }}>
                        <p className="title text-white text-left" style={{ margin: 0 }}>
                          <MarqueeText
                            text={cleanText(item.artist)}
                            textStyle={{ fontWeight: "bold" }}
                          />
                        </p>
                        <p className="title text-white text-left" style={{ margin: 0 }}>
                          <MarqueeText text={cleanText(item.track)} />
                        </p>
                        <p
                          className="text-left text-gray-400"
                          style={{ fontSize: 12, margin: 0 }}
                        >
                          {item.genre || "Unknown Genre"}
                        </p>
                        {isGreyedOut && (
                          <p className="text-xs text-red-400 text-left mt-1">
                            Low rating
                          </p>
                        )}
                      </div>
                      {!item.default && (
                        <div
                          className="flex flex-row items-center ml-2"
                          style={{ flexShrink: 0 }}
                        >
                          <div className="flex flex-row items-center mr-3">
                            <button
                              onClick={(e) => {
                                upvote(item);
                                resetTimer();
                              }}
                            >
                              <svg
                                width="20px"
                                height="20px"
                                viewBox="0 0 24 24"
                                fill={
                                  upObj[item.id] && upObj[item.id] > 0
                                    ? "#0000FF"
                                    : "#FFFFFF"
                                }
                                fill-opacity={
                                  upObj[item.id] && upObj[item.id] > 0 ? "1" : "0"
                                }
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M7.99997 20H17.1919C17.9865 20 18.7058 19.5296 19.0243 18.8016L21.8323 12.3833C21.9429 12.1305 22 11.8576 22 11.5816V11C22 9.89543 21.1045 9 20 9H13.5L14.7066 4.5757C14.8772 3.95023 14.5826 3.2913 14.0027 3.00136V3.00136C13.4204 2.7102 12.7134 2.87256 12.3164 3.3886L8.41472 8.46082C8.14579 8.81044 7.99997 9.23915 7.99997 9.68024V20ZM7.99997 20H2V10H7.99997V20Z"
                                  stroke="#FFFFFF"
                                  stroke-width="2"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                            </button>
                            <div className="ml-1">{upObj[item.id] || 0}</div>
                          </div>
                          <div className="flex flex-row items-center">
                            <button
                              onClick={(e) => {
                                downvote(item);
                                resetTimer();
                              }}
                            >
                              <svg
                                width="20px"
                                height="20px"
                                viewBox="0 0 24 24"
                                fill={
                                  downObj[item.id] && downObj[item.id] > 0
                                    ? "#FF0000"
                                    : ""
                                }
                                fill-opacity={
                                  downObj[item.id] && downObj[item.id] > 0 ? "1" : "0"
                                }
                                xmlns="http://www.w3.org/2000/svg"
                                transform="scale(-1, 1)"
                              >
                                <path
                                  d="M7.99997 4H17.1919C17.9865 4 18.7058 4.47039 19.0243 5.19836L21.8323 11.6167C21.9429 11.8695 22 12.1424 22 12.4184V13C22 14.1046 21.1045 15 20 15H13.5L14.7066 19.4243C14.8772 20.0498 14.5826 20.7087 14.0027 20.9986V20.9986C13.4204 21.2898 12.7134 21.1274 12.3164 20.6114L8.41472 15.5392C8.14579 15.1896 7.99997 14.7608 7.99997 14.3198V14M7.99997 4H2V14H7.99997M7.99997 4V14"
                                  stroke="#FFFFFF"
                                  stroke-width="2"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                            </button>
                            <div className="ml-1">{downObj[item.id] || 0}</div>
                          </div>
                        </div>
                      )}
                    </div>
                    ) : (
                    <div
                      key={i}
                      ref={(el) => { if (el) itemRefs.current[item.id] = el; }}
                      className={highlightId && highlightId === item.id ? "muuz-newreq-flash" : undefined}
                    >
                      <div
                        className="post flex flex-row justify-between text-left text-white"
                        style={{
                          opacity: isGreyedOut ? 0.4 : 1,
                          transition: 'opacity 0.3s ease'
                        }}
                      >
                        <img
                          src={item.coverart || FALLBACK_COVERART}
                          alt="cover art"
                          className="w-full lg:w-1/2 rounded-full mr-4"
                          style={{
                            width: "64px",
                            height: "64px",
                            filter: isGreyedOut ? 'grayscale(100%)' : 'none'
                          }}
                          onError={(e) => {
                            const src = e.target.getAttribute('data-original-src');
                            if (!src) {
                              e.target.setAttribute('data-original-src', e.target.src);
                              e.target.src = FALLBACK_COVERART;
                              setTimeout(() => {
                                e.target.src = e.target.getAttribute('data-original-src');
                              }, 3000);
                            } else {
                              e.target.onerror = null;
                              e.target.src = FALLBACK_COVERART;
                            }
                          }}
                        />
                        <div className="flex-grow" style={{ minWidth: 0, textAlign: "left" }}>
                          <p className="title text-white text-left">
                            <MarqueeText
                              text={cleanText(item.artist)}
                              textStyle={{ fontWeight: "bold" }}
                            />
                          </p>
                          <p className="title text-white text-left">
                            <MarqueeText text={cleanText(item.track)} />
                          </p>
                          <p className="text-white text-left text-gray-400">
                            {item.genre || "Unknown Genre"}
                          </p>
                          {isGreyedOut && (
                            <p className="text-xs text-red-400 text-left mt-1">
                              Low rating
                            </p>
                          )}
                        </div>
                        {/* Hide voting buttons for the default next track */}
                        {!item.default && (
                          <div className="flex flex-col mt-0">
                            <div className="flex flex-row items-center mb-4">
                              <button
                                onClick={(e) => {
                                  upvote(item);
                                  resetTimer();
                                }}
                              >
                                <svg
                                  width="20px"
                                  height="20px"
                                  viewBox="0 0 24 24"
                                  fill={
                                    upObj[item.id] && upObj[item.id] > 0
                                      ? "#0000FF"
                                      : "#FFFFFF"
                                  }
                                  fill-opacity={
                                    upObj[item.id] && upObj[item.id] > 0
                                      ? "1"
                                      : "0"
                                  }
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M7.99997 20H17.1919C17.9865 20 18.7058 19.5296 19.0243 18.8016L21.8323 12.3833C21.9429 12.1305 22 11.8576 22 11.5816V11C22 9.89543 21.1045 9 20 9H13.5L14.7066 4.5757C14.8772 3.95023 14.5826 3.2913 14.0027 3.00136V3.00136C13.4204 2.7102 12.7134 2.87256 12.3164 3.3886L8.41472 8.46082C8.14579 8.81044 7.99997 9.23915 7.99997 9.68024V20ZM7.99997 20H2V10H7.99997V20Z"
                                    stroke="#FFFFFF"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                </svg>
                              </button>
                              <div className="ml-2">{upObj[item.id] || 0}</div>
                            </div>

                            <div className="flex flex-row items-center mb-4">
                              <button
                                onClick={(e) => {
                                  downvote(item);
                                  resetTimer();
                                }}
                              >
                                <svg
                                  width="20px"
                                  height="20px"
                                  viewBox="0 0 24 24"
                                  fill={
                                    downObj[item.id] && downObj[item.id] > 0
                                      ? "#FF0000"
                                      : ""
                                  }
                                  fill-opacity={
                                    downObj[item.id] && downObj[item.id] > 0
                                      ? "1"
                                      : "0"
                                  }
                                  xmlns="http://www.w3.org/2000/svg"
                                  transform="scale(-1, 1)"
                                >
                                  <path
                                    d="M7.99997 4H17.1919C17.9865 4 18.7058 4.47039 19.0243 5.19836L21.8323 11.6167C21.9429 11.8695 22 12.1424 22 12.4184V13C22 14.1046 21.1045 15 20 15H13.5L14.7066 19.4243C14.8772 20.0498 14.5826 20.7087 14.0027 20.9986V20.9986C13.4204 21.2898 12.7134 21.1274 12.3164 20.6114L8.41472 15.5392C8.14579 15.1896 7.99997 14.7608 7.99997 14.3198V14M7.99997 4H2V14H7.99997M7.99997 4V14"
                                    stroke="#FFFFFF"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                </svg>
                              </button>
                              <div className="ml-2">{downObj[item.id] || 0}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}

                  <MoodSelector
                    clientObj={clientObj}
                    moodButtonDailyLimit={clientObj?.moodButtonDailyLimit || 1}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* <div className="tab-content active" id="home">
            <h2>
              Welcome to Brooklyn Bar
              <br />
              Powered by MuuzBox
            </h2>

            <div className="post text-left text-white">
              <p className="title  text-white">
                <strong>How to Use MuuzBox:</strong>
              </p>
              <ul className="list-disc text-left ml-4">
                <li className="text-left">
                  Home: See what's trending and & updates
                </li>
                <li className="text-left">
                  Now Playing: Current playlist + vote on upcoming tracks
                </li>
                <li className="text-left">
                  Request: Search & request any song you want
                </li>
                <li className="text-left">
                  Chats: Talk music with other guests
                </li>
                <li className="text-left">
                  Profile: Manage your account & preferences
                </li>
              </ul>
            </div>

            <div className="post">
              <img
                src="https://elitebybloshotel.com/wp-content/uploads/2022/11/brooklyn_promotions_hh.jpg"
                alt=""
              />
              <p className="title text-white">Happy Hour, Big Discounts!</p>
            </div>
            <div className="post">
              <img
                src="https://elitebybloshotel.com/brooklyn-bar-dubai/wp-content/themes/brooklyn/assets/images/sports/champions_league.webp"
                alt=""
              />
              <p className="title  text-white">
                Watch It Live: PSG vs Inter Milan
              </p>
            </div>
            <div className="post">
              <p className="title text-left text-white">
                🍽 Ribs & Drinks for AED 140
                <br />
                Every Tuesday from 5pm - 1am
              </p>
            </div>
            <div className=" post text-left text-white">
              🎧 Top voted song: "Blinding Lights" - The Weeknd
            </div>
            <div className="post text-left text-white">
              🎵 Join the community and vote for your favorite tracks!
            </div>
            <div className="post text-left text-white">
              🚀 New features coming soon - stay tuned!
            </div>
          </div> */}
        </div>
      </div>

      {showVoteReminder && createPortal(
        <div style={{ position: "fixed", inset: 0, zIndex: 99998, pointerEvents: "none", overflow: "hidden" }}>
          {[0, 1, 2].map((n) => (
            <div key={n} className="muuz-vote-float" style={{ left: `${25 + n * 25}%`, animationDelay: `${n * 0.28}s` }}>
              <svg
                width={n % 2 === 0 ? 54 : 40}
                height={n % 2 === 0 ? 54 : 40}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.99997 20H17.1919C17.9865 20 18.7058 19.5296 19.0243 18.8016L21.8323 12.3833C21.9429 12.1305 22 11.8576 22 11.5816V11C22 9.89543 21.1045 9 20 9H13.5L14.7066 4.5757C14.8772 3.95023 14.5826 3.2913 14.0027 3.00136C13.4204 2.7102 12.7134 2.87256 12.3164 3.3886L8.41472 8.46082C8.14579 8.81044 7.99997 9.23915 7.99997 9.68024V20ZM7.99997 20H2V10H7.99997V20Z"
                  stroke="#ffffff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          ))}
          <div className="muuz-vote-hint">👍 Tap to vote for songs in the queue</div>
        </div>,
        document.body
      )}

      {showLyrics && createPortal(
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "tween", duration: 0.25 }}
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            maxHeight: "75vh",
            background: "#101010",
            color: "#fff",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            boxShadow: "0 -8px 24px rgba(0,0,0,0.5)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "8px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <strong style={{ fontSize: "0.95rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {cleanText(currentSongObj?.track) || "Lyrics"}
              </strong>
              <span style={{ fontSize: "0.78rem", opacity: 0.7 }}>
                {cleanText(currentSongObj?.artist)}
                {lyricsData?.source === "lrclib" && " · LRCLIB"}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShowLyrics(false)}
              aria-label="Close lyrics"
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                fontSize: "1.4rem",
                cursor: "pointer",
                padding: "4px 10px",
              }}
            >
              ✕
            </button>
          </div>

          <div
            style={{
              padding: "12px 16px 20px",
              overflowY: "auto",
              flexGrow: 1,
              lineHeight: 1.6,
              fontSize: "0.95rem",
            }}
          >
            {lyricsLoading && (
              <div style={{ opacity: 0.7 }}>Loading lyrics…</div>
            )}
            {!lyricsLoading && lyricsError && (
              <div style={{ color: "#ff6b6b" }}>{lyricsError}</div>
            )}
            {!lyricsLoading && !lyricsError && lyricsData && !lyricsData.found && (
              <div style={{ opacity: 0.7 }}>
                Lyrics not available for this track.
              </div>
            )}
            {!lyricsLoading && !lyricsError && lyricsData?.found && lrcLines.length > 0 && (
              <div>
                {lrcLines.map((ln, i) => (
                  <div
                    key={i}
                    ref={(el) => (lrcLineRefs.current[i] = el)}
                    style={{
                      padding: "4px 0",
                      opacity: i === lrcIndex ? 1 : 0.5,
                      fontWeight: i === lrcIndex ? 700 : 400,
                      transition: "opacity 0.2s, font-weight 0.2s",
                    }}
                  >
                    {ln.text || "♪"}
                  </div>
                ))}
              </div>
            )}
            {!lyricsLoading && !lyricsError && lyricsData?.found && lrcLines.length === 0 && (
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  fontFamily: "inherit",
                  margin: 0,
                }}
              >
                {lyricsData.plainLyrics || ""}
              </pre>
            )}
          </div>
        </motion.div>,
        document.body
      )}
    </>
  );
};
